import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import { createPinia } from 'pinia'
import mitt from 'mitt'
import { useSearchStore } from '@/stores/searchStore'

import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap/dist/js/bootstrap.bundle.min.js'
import '@/assets/bulma-switch.min.css'
import '@/assets/theme.css'
import '@/assets/styles.css'
import '@/assets/invoice-dashboard.css'
import '@vuepic/vue-datepicker/dist/main.css'
import 'vue-good-table-next/dist/vue-good-table-next.css'

// TOAST NOTIFICATIONS
import VueToast from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { cancelAll } from '@/services/abortManager'

// Import Font Awesome core
import { library } from '@fortawesome/fontawesome-svg-core'

// Import specific icons (example)
import {
    faEye,
    faEyeSlash,
    faHouse,
    faMagnifyingGlass,
    faBars,
    faAngleDown,
    faAngleUp,
    faStar,
    faBell,
    faMoon,
    faSun,
    faUser,
    faCircleInfo,
    faPlus,
    faChevronUp,
    faChevronDown,
    faChevronRight,
    faChevronLeft,
    faRightFromBracket,
    faDownload,
    faEdit,
    faTachometerAlt,
    faCopy,
    faArrowsRotate,
    faSitemap,
    faClipboardList,
    faBolt,
    faRotateLeft,
    faWandMagicSparkles,
    faIdCard,
    faCircleExclamation,
    faCircleCheck,
    faCircleXmark,
    faChartLine,
    faChartArea,
    faLink,
    faFileInvoice,
    faShoppingCart,
    faChartBar,
    faList,
    faScaleBalanced,
    faChartPie,
    faTags,
    faCubes,
    faClockRotateLeft,
    faUpload,
    faTruck,
    faShip,
    faLinkSlash,
    faFlask,
    faFileSignature,
    faFileInvoiceDollar,
    faCircleQuestion,
    faBookOpen,
    faCoffee,
    faMinus,
    faKeyboard,
    faSliders,
    faTowerBroadcast,
    faPlaneDeparture,
    faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons'

// Import the FontAwesomeIcon component
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

// Add icons to the library
library.add(faEye)
library.add(faEyeSlash)
library.add(faHouse)
library.add(faMagnifyingGlass)
library.add(faBars)
library.add(faAngleDown)
library.add(faAngleUp)
library.add(faStar)
library.add(faBell)
library.add(faMoon)
library.add(faSun)
library.add(faUser)
library.add(faCircleInfo)
library.add(faPlus)
library.add(faChevronUp)
library.add(faChevronDown)
library.add(faChevronRight)
library.add(faChevronLeft)
library.add(faRightFromBracket)
library.add(faDownload)
library.add(faEdit)
library.add(faTachometerAlt)
library.add(faCopy)
library.add(faArrowsRotate)
library.add(faSitemap)
library.add(faClipboardList)
library.add(faBolt)
library.add(faRotateLeft)
library.add(faWandMagicSparkles)
library.add(faIdCard)
library.add(faCircleExclamation)
library.add(faCircleCheck)
library.add(faCircleXmark)
library.add(faChartLine)
library.add(faChartArea)
library.add(faLink)
library.add(faFileInvoice)
library.add(faShoppingCart)
library.add(faChartBar)
library.add(faList)
library.add(faScaleBalanced)
library.add(faChartPie)
library.add(faTags)
library.add(faCubes)
library.add(faClockRotateLeft)
library.add(faUpload)
library.add(faTruck)
library.add(faShip)
library.add(faLinkSlash)
library.add(faFlask)
library.add(faFileSignature)
library.add(faFileInvoiceDollar)
library.add(faCircleQuestion)
library.add(faBookOpen)
library.add(faCoffee)
library.add(faMinus)
library.add(faKeyboard)
library.add(faSliders)
library.add(faTowerBroadcast)
library.add(faPlaneDeparture)
library.add(faArrowUpRightFromSquare)

const app = createApp(App)

const emitter = mitt()
app.config.globalProperties.emitter = emitter

// Pinia store
const pinia = createPinia()

app.use(pinia)
app.use(router)
app.mixin({
    computed: {
        searchStore() {
            return useSearchStore()
        },
    },
})
app.use(VueToast)

// Register the FontAwesomeIcon component globally
app.component('font-awesome-icon', FontAwesomeIcon)

app.config.errorHandler = (err, vm, info) => {
    console.log('err', err)
    console.error(`Error**: ${err.toString()}\nInfo: ${info}`)
}

// Abort any lingering streams on unload or long inactivity.
const idleLimitMs = 6 * 60 * 60 * 1000 // 6 hours
let idleTimer
const resetIdleTimer = () => {
    clearTimeout(idleTimer)
    idleTimer = setTimeout(() => cancelAll(), idleLimitMs)
}
;['mousemove', 'keydown', 'click', 'touchstart', 'scroll', 'visibilitychange'].forEach((evt) =>
    window.addEventListener(
        evt,
        () => {
            // Only reset on user activity or when tab becomes visible again.
            if (evt === 'visibilitychange' && document.hidden) return
            resetIdleTimer()
        },
        { passive: true }
    )
)
window.addEventListener('beforeunload', () => cancelAll())
resetIdleTimer()

app.mount('#app')
